/**
 * Deferred deletion with an undo window (mock's undo snackbar): deleting a
 * life story cascades through the vault (PDF, recordings, permissions) and
 * cannot be reversed server-side, so the DELETE request itself waits out the
 * undo window. Undo simply cancels the timer; nothing was sent yet.
 *
 * Module-level because the flow unmounts on exit while the timer must keep
 * running, and the undo bar lives on whichever page the user lands on.
 */

import {useSyncExternalStore} from 'react'

export const UNDO_WINDOW_MS = 8000

let pending = null // {ethicalWillId, firstGroupId, message, performDelete, timerId, committing}
let snapshot = null
const listeners = new Set()

const notify = () => {
  snapshot = pending ?
    {
      ethicalWillId: pending.ethicalWillId,
      firstGroupId: pending.firstGroupId,
      message: pending.message,
      committing: pending.committing
    } :
    null

  listeners.forEach(listener => listener(snapshot))
}

export const subscribeToPendingDeletion = listener => {
  listeners.add(listener)

  return () => listeners.delete(listener)
}

export const pendingLifeStoryDeletion = () => snapshot

/** Sends the pending DELETE now. The story stays hidden until the request settles. */
export const commitPendingDeletion = () => {
  if (!pending || pending.committing) return

  clearTimeout(pending.timerId)
  pending.committing = true
  notify()

  // Scheduling another deletion while this DELETE is in flight replaces
  // `pending`; settling must only clear the deletion it belongs to.
  const committed = pending

  const finish = () => {
    if (pending !== committed) return

    pending = null
    notify()
  }

  // A failed DELETE (rejected or thrown synchronously) leaves the story
  // intact on the server; it reappears on the next fetch, which is the safe
  // direction to fail in. performDelete stays synchronous so the pagehide
  // commit can still get the request out before the page unloads.
  let deleteSettled

  try {
    deleteSettled = Promise.resolve(committed.performDelete())
  } catch {
    deleteSettled = Promise.resolve()
  }

  deleteSettled.then(finish, finish)
}

/**
 * Hides the story and starts the undo window. Only one deletion can be
 * pending at a time: scheduling another commits the previous one immediately.
 *
 * `firstGroupId` is the story's vault response group (null for unpublished
 * drafts): vault surfaces (the category page row, the item modal) match on it
 * to keep the doomed record out of sight during the window.
 */
export const scheduleLifeStoryDeletion = ({ethicalWillId, firstGroupId = null, message, performDelete}) => {
  commitPendingDeletion()

  pending = {ethicalWillId, firstGroupId, message, performDelete, committing: false}
  pending.timerId = setTimeout(commitPendingDeletion, UNDO_WINDOW_MS)
  notify()
}

/** Cancels the pending deletion; false when there's nothing left to undo (already committing). */
export const undoPendingDeletion = () => {
  if (!pending || pending.committing) return false

  clearTimeout(pending.timerId)
  pending = null
  notify()

  return true
}

export const usePendingLifeStoryDeletion = () =>
  useSyncExternalStore(subscribeToPendingDeletion, pendingLifeStoryDeletion)

// Leaving the app inside the undo window still means "delete": fire the
// request while the page can still send it.
if (typeof window !== 'undefined')
  window.addEventListener('pagehide', () => commitPendingDeletion())
